import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { map } from "rxjs/operators";

import { PropertiesService } from "@app/core/services/properties.service";
import { User, UserResponse, UserWarning } from "@app/core/domain";
import { DateService } from "@app/core/services/date.service";

function retrieveLang(value: string) {
  return value.substr(0, 2);
}

export interface UserApiClientInterface {
  getCurrentUser(): Observable<User>;
}

@Injectable()
export class UserApiClient implements UserApiClientInterface {
  constructor(
    public http: HttpClient,
    private properties: PropertiesService,
    private dateService: DateService
  ) {}

  getCurrentUser(): Observable<User> {
    try {
      const securedApiUrl = this.properties.get("securedApiUrl");
      const url = `${securedApiUrl}/user`;

      return this.http
        .get<UserResponse>(url)
        .pipe(map((res: UserResponse) => this.userResponseToUser(res)));
    } catch (error) {
      return throwError(error);
    }
  }

  /**
   * Call backend logout endpoint to clear server-side session
   * This clears the HttpOnly INGO_SESSION_ID cookie
   */
  logout(): Observable<void> {
    try {
      const securedApiUrl = this.properties.get("securedApiUrl");

      if (!securedApiUrl || typeof securedApiUrl !== "string") {
        return throwError(
          new Error(
            "Property securedApiUrl not loaded. Cannot call logout endpoint."
          )
        );
      }

      const baseUrl = securedApiUrl.replace("/secured", "/logout");

      return this.http.post<void>(baseUrl, {});
    } catch (error) {
      return throwError(error);
    }
  }

  private resolveWarning(warnings: string[]): UserWarning | null {
    if (warnings.includes("EMPTY_BIRTHDAY")) return UserWarning.EMPTY_BIRTHDAY;
    if (warnings.includes("TOO_YOUNG")) return UserWarning.TOO_YOUNG;
    if (warnings.includes("MARKETING_CONSENT")) return UserWarning.MARKETING_CONSENT;
    return null;
  }

  private userResponseToUser(res: UserResponse): User {
    return {
      id: res.id,
      oktaId: res.oktaId,
      name: res.name,
      email: res.email,
      firstName: res.givenName,
      lastName: res.familyName,
      birthDate: this.dateService.stringToSimpleDate(res.birthDate),
      phone: res.phoneNumber,
      containsPlcCard: res.containsPlcCard,
      countryCode: res.countryCode.toLowerCase(),
      lang: retrieveLang(res.lang).toLowerCase(),
      warning: this.resolveWarning(res.warnings),
    };
  }
}
